// High-end client-side generators for ITO festival & company marketing assets.
// Each piece is rendered on a high-resolution canvas using real web fonts
// (Fraunces display serif + Manrope sans), then exported as PNG / placed into a
// jsPDF page — giving full typographic control and a print-grade, elegant result.
import jsPDF from "jspdf";
import { CONTACT, SINCE, SERVICES, FLEET, STATS, IMAGES } from "./marketingData";

export const WEBSITE = "itoworldwide.com";
export const WEBSITE2 = "itotransportvehicles.com";
const BLUE = "#164FB3";
const YELLOW = "#F5C21B";
export const TAGLINE =
  "Pan-India transport to every pincode  ·  All types of vehicles  ·  Fastest, safest & most reliable transporter in India";

// ---- palette ----
const INK = "#17130F";
const INK_SOFT = "#4A423B";
const CREAM = "#F6F1E8";
const CREAM_2 = "#EFE8DA";
const GOLD = "#C4A24C";
const BRAND = "#B5451F";
const LINE = "#D9CFBE";
const DISPLAY = "Fraunces";
const SANS = "Manrope";
const LOGO = "/ito-logo.png";

const A4_W = 1240, A4_H = 1754;            // 150 dpi A4 portrait
const S = A4_W / 210;                       // px per mm
const fileName = (f, s) => `ITO ${String(f.name).replace(/[^\w& ]+/g, "").trim()} - ${s}`;
// Deterministic per-festival layout variant so each greeting looks distinct (not one template).
function variantOf(key) { let h = 0; for (const ch of String(key)) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return h % 3; }

// ---- fonts ----
const FB = "https://cdn.jsdelivr.net/npm/@fontsource";
const FONT_DEFS = [
  ["Fraunces", "600", `${FB}/fraunces/files/fraunces-latin-600-normal.woff2`],
  ["Fraunces", "700", `${FB}/fraunces/files/fraunces-latin-700-normal.woff2`],
  ["Fraunces", "500", `${FB}/fraunces/files/fraunces-latin-500-normal.woff2`],
  ["Manrope", "400", `${FB}/manrope/files/manrope-latin-400-normal.woff2`],
  ["Manrope", "500", `${FB}/manrope/files/manrope-latin-500-normal.woff2`],
  ["Manrope", "600", `${FB}/manrope/files/manrope-latin-600-normal.woff2`],
  ["Manrope", "700", `${FB}/manrope/files/manrope-latin-700-normal.woff2`],
  ["Manrope", "800", `${FB}/manrope/files/manrope-latin-800-normal.woff2`],
];
let fontsReady = null;
function ensureFonts() {
  if (fontsReady) return fontsReady;
  fontsReady = Promise.all(FONT_DEFS.map(async ([fam, wt, url]) => {
    try { const ff = new FontFace(fam, `url(${url})`, { weight: wt, display: "swap" }); await ff.load(); document.fonts.add(ff); }
    catch { /* fall back to system serif/sans */ }
  })).then(() => { try { return document.fonts.ready; } catch { return null; } });
  return fontsReady;
}

function loadImage(url) {
  return new Promise((res) => {
    if (!url) return res(null);
    const img = new Image(); img.crossOrigin = "anonymous";
    img.onload = () => res(img); img.onerror = () => res(null); img.src = url;
  });
}
function newCanvas(w, h) { const c = document.createElement("canvas"); c.width = w; c.height = h; return c; }
function drawCover(ctx, img, x, y, w, h) {
  const ir = img.width / img.height, r = w / h;
  let sw = img.width, sh = img.height, sx = 0, sy = 0;
  if (ir > r) { sw = img.height * r; sx = (img.width - sw) / 2; } else { sh = img.width / r; sy = (img.height - sh) / 2; }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}
function fill(ctx, x, y, w, h, col) { ctx.fillStyle = col; ctx.fillRect(x, y, w, h); }
function vGrad(ctx, x, y, w, y0, y1, stops) {
  const g = ctx.createLinearGradient(0, y0, 0, y1); stops.forEach(([o, c]) => g.addColorStop(o, c));
  ctx.fillStyle = g; ctx.fillRect(x, y, w, y1 - y0);
}
function setFont(ctx, fam, wt, px, tracking = 0) {
  ctx.font = `${wt} ${px}px ${fam}, ${fam === DISPLAY ? "Georgia, serif" : "Arial, sans-serif"}`;
  try { ctx.letterSpacing = `${tracking}px`; } catch { /* older browsers */ }
}
function label(ctx, text, x, y, col, align = "left") {
  setFont(ctx, SANS, "700", 22, 4); ctx.fillStyle = col; ctx.textAlign = align; ctx.textBaseline = "alphabetic";
  ctx.fillText(String(text).toUpperCase(), x, y);
  try { ctx.letterSpacing = "0px"; } catch { /* noop */ }
}
function line(ctx, x1, y, x2, col, w = 1) { ctx.strokeStyle = col; ctx.lineWidth = w; ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke(); }
// wrapped paragraph; returns bottom y
function para(ctx, text, x, y, maxW, { size = 30, lh = 44, weight = "400", fam = SANS, col = INK, align = "left", maxLines = 0 } = {}) {
  setFont(ctx, fam, weight, size); ctx.fillStyle = col; ctx.textAlign = align; ctx.textBaseline = "alphabetic";
  const words = String(text).split(" "); let ln = "", lines = [];
  for (const w of words) { const t = ln ? ln + " " + w : w; if (ctx.measureText(t).width > maxW && ln) { lines.push(ln); ln = w; } else ln = t; }
  if (ln) lines.push(ln);
  if (maxLines && lines.length > maxLines) { lines = lines.slice(0, maxLines); lines[maxLines - 1] = lines[maxLines - 1].replace(/[.,]?\s*\S*$/, "…"); }
  const ax = align === "center" ? x : align === "right" ? x : x;
  lines.forEach((l, i) => ctx.fillText(l, ax, y + i * lh));
  return y + (lines.length - 1) * lh;
}
async function logoLockup(ctx, x, y, onDark) {
  const logo = await loadImage(LOGO);
  const s = 96;
  if (logo) { if (onDark) { fill(ctx, x, y, s, s, "#ffffff"); ctx.drawImage(logo, x + 6, y + 6, s - 12, s - 12); } else ctx.drawImage(logo, x, y, s, s); }
  const tx = x + s + 26;
  setFont(ctx, SANS, "800", 30, 1); ctx.fillStyle = onDark ? "#fff" : INK; ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  ctx.fillText("INDIAN TRANSPORT", tx, y + 40);
  ctx.fillStyle = onDark ? GOLD : BRAND; ctx.fillText("ORGANISATION", tx, y + 74);
  try { ctx.letterSpacing = "0px"; } catch { /* noop */ }
  setFont(ctx, SANS, "500", 21); ctx.fillStyle = onDark ? "rgba(255,255,255,.75)" : INK_SOFT;
  ctx.fillText(`EST. ${SINCE}  ·  ${WEBSITE}`, tx, y + 100);
}
function footerBand(ctx, W, H) {
  const top = H - 200;
  line(ctx, 104, top, W - 104, LINE, 2);
  fill(ctx, 104, top + 30, 54, 6, BRAND);
  ctx.textBaseline = "alphabetic";
  setFont(ctx, SANS, "800", 34, 1); ctx.fillStyle = INK; ctx.textAlign = "left";
  ctx.fillText(`+91 ${CONTACT.phone}`, 104, top + 92);
  try { ctx.letterSpacing = "0px"; } catch { /* noop */ }
  setFont(ctx, SANS, "500", 25); ctx.fillStyle = INK_SOFT;
  ctx.fillText(`${CONTACT.email}   ·   ${WEBSITE}`, 104, top + 134);
  para(ctx, CONTACT.address, 104, top + 168, W - 208, { size: 22, lh: 30, col: INK_SOFT });
}

// festival banner (image + scrim + logo + greeting), returns banner bottom y
async function festivalBanner(ctx, f, W, bh, accent, variant = 0) {
  const img = await loadImage(f.image);
  if (img) drawCover(ctx, img, 0, 0, W, bh); else fill(ctx, 0, 0, W, bh, accent);
  vGrad(ctx, 0, 0, W, 0, 300, [[0, "rgba(12,10,8,0.6)"], [1, "rgba(12,10,8,0)"]]);
  vGrad(ctx, 0, bh - 520, W, bh - 520, bh, [[0, "rgba(12,10,8,0)"], [0.55, "rgba(12,10,8,0.55)"], [1, "rgba(12,10,8,0.94)"]]);
  await logoLockup(ctx, 104, 92, true);
  const centered = variant === 1;
  const ax = centered ? W / 2 : 104;
  const al = centered ? "center" : "left";
  label(ctx, f.eyebrow || "Season's greetings from ITO", ax, bh - 258, accent, al);
  if (centered) line(ctx, W / 2 - 70, bh - 240, W / 2 + 70, accent, 3);
  para(ctx, f.name, ax, bh - 150, W - 208, { fam: DISPLAY, weight: "700", size: 118, lh: 118, col: "#fff", align: al, maxLines: 2 });
}

function saveCanvasesAsPdf(canvases, filename) {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  canvases.forEach((c, i) => { if (i) doc.addPage(); doc.addImage(c.toDataURL("image/jpeg", 0.94), "JPEG", 0, 0, 210, 297); });
  doc.save(filename);
}
function triggerDownload(href, name) { const a = document.createElement("a"); a.href = href; a.download = name; document.body.appendChild(a); a.click(); a.remove(); }

// ============ 1) WhatsApp square 1080×1080 ============
export async function downloadWhatsApp(f) {
  await ensureFonts();
  const Z = 1080;
  const c = newCanvas(Z, Z), ctx = c.getContext("2d");
  const img = await loadImage(f.image);
  fill(ctx, 0, 0, Z, Z, CREAM);
  if (img) drawCover(ctx, img, 0, 0, Z, Z);
  vGrad(ctx, 0, 0, Z, 0, 360, [[0, "rgba(12,10,8,0.62)"], [1, "rgba(12,10,8,0)"]]);
  const v = variantOf(f.name || "");
  const accent = f.accent || BRAND;
  const centered = v === 1;
  if (centered) fill(ctx, 0, 0, Z, Z, "rgba(14,11,9,0.5)");
  else vGrad(ctx, 0, 470, Z, 470, Z, [[0, "rgba(12,10,8,0)"], [0.5, "rgba(12,10,8,0.55)"], [1, "rgba(12,10,8,0.95)"]]);
  await logoLockup(ctx, 60, 58, true);
  const ax = centered ? Z / 2 : 60;
  const al = centered ? "center" : "left";
  const gY = centered ? 548 : (v === 2 ? 628 : 672);
  label(ctx, f.eyebrow || "Season's greetings from ITO", ax, gY - 112, accent, al);
  if (centered) line(ctx, Z / 2 - 64, gY - 92, Z / 2 + 64, accent, 3);
  para(ctx, f.name, ax, gY, Z - 120, { fam: DISPLAY, weight: "700", size: 88, lh: 90, col: "#fff", align: al, maxLines: 2 });
  ctx.textBaseline = "alphabetic";
  para(ctx, f.quote, ax, gY + 128, Z - 120, { fam: DISPLAY, weight: "500", size: 28, lh: 40, col: "rgba(255,255,255,0.92)", align: al, maxLines: 3 });
  // Full head-office contact over the photo's soft scrim — no coloured bar.
  line(ctx, 60, Z - 176, Z - 60, "rgba(255,255,255,0.30)", 2);
  ctx.textBaseline = "alphabetic";
  setFont(ctx, SANS, "800", 32, 1); ctx.fillStyle = "#fff"; ctx.textAlign = "left";
  ctx.fillText(`+91 ${CONTACT.phone}`, 60, Z - 128);
  try { ctx.letterSpacing = "0px"; } catch { /* noop */ }
  setFont(ctx, SANS, "600", 26); ctx.fillStyle = GOLD; ctx.textAlign = "right";
  ctx.fillText(WEBSITE, Z - 60, Z - 128);
  ctx.textAlign = "left";
  setFont(ctx, SANS, "500", 24); ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.fillText(CONTACT.email, 60, Z - 86);
  setFont(ctx, SANS, "400", 22); ctx.fillStyle = "rgba(255,255,255,0.82)";
  ctx.fillText(CONTACT.address, 60, Z - 50);
  c.toBlob((b) => triggerDownload(URL.createObjectURL(b), fileName(f, "WhatsApp") + ".png"), "image/png");
}

// ============ 2) Flyer — A4 portrait poster ============
export async function downloadFlyer(f) {
  await ensureFonts();
  const accent = f.accent || BRAND, W = A4_W, H = A4_H;
  const c = newCanvas(W, H), ctx = c.getContext("2d");
  fill(ctx, 0, 0, W, H, CREAM);
  const bh = 840;
  await festivalBanner(ctx, f, W, bh, accent, variantOf(f.name));
  let y = bh + 118;
  y = para(ctx, `“${f.quote}”`, 104, y, W - 208, { fam: DISPLAY, weight: "500", size: 40, lh: 54, col: INK, maxLines: 3 });
  y += 58; line(ctx, 104, y, 300, accent, 4); y += 58;
  y = para(ctx, "Fastest. Safest. Most reliable transporter in India.", 104, y, W - 208, { fam: DISPLAY, weight: "600", size: 42, lh: 52, col: accent, maxLines: 2 });
  y += 70;
  y = para(ctx, "Pan-India transport to every pincode by road & air — all types of vehicles: air-suspension & low-bed trailers, explosive vans, reefers, trucks and city tempos.", 104, y, W - 208, { size: 27, lh: 40, col: INK_SOFT, maxLines: 3 });
  y += 86;
  const stats = STATS.slice(0, 4);
  const cw = (W - 208) / stats.length;
  stats.forEach((s, i) => {
    const x = 104 + i * cw;
    setFont(ctx, DISPLAY, "700", 62); ctx.fillStyle = accent; ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
    ctx.fillText(`${s.value}${s.suffix}`, x, y);
    para(ctx, s.label, x, y + 38, cw - 24, { size: 21, lh: 27, col: INK_SOFT, maxLines: 2 });
  });
  footerBand(ctx, W, H);
  saveCanvasesAsPdf([c], fileName(f, "Flyer") + ".pdf");
}

// ============ 3) Brochure — A4 one-pager (greeting + services + contact) ============
export async function downloadBrochure(f) {
  await ensureFonts();
  const accent = f.accent || BRAND, W = A4_W, H = A4_H;
  const c = newCanvas(W, H), ctx = c.getContext("2d");
  fill(ctx, 0, 0, W, H, CREAM);
  const bh = 520;
  await festivalBanner(ctx, f, W, bh, accent, variantOf(f.name));
  let y = bh + 84;
  y = para(ctx, `“${f.quote}”`, 104, y, W - 208, { fam: DISPLAY, weight: "500", size: 28, lh: 40, col: INK, maxLines: 3 });
  y += 50;
  label(ctx, "What we move for you", 104, y, accent); y += 42;
  SERVICES.slice(0, 5).forEach((s, i) => {
    setFont(ctx, DISPLAY, "700", 36); ctx.fillStyle = accent; ctx.textBaseline = "alphabetic"; ctx.textAlign = "left";
    ctx.fillText(`${String(i + 1).padStart(2, "0")}`, 104, y);
    para(ctx, s.title, 186, y, W - 290, { fam: DISPLAY, weight: "600", size: 31, lh: 37, col: INK });
    y = para(ctx, s.short, 186, y + 36, W - 290, { size: 23, lh: 30, col: INK_SOFT, maxLines: 2 });
    y += 30; line(ctx, 104, y, W - 104, LINE, 1); y += 30;
  });
  footerBand(ctx, W, H);
  saveCanvasesAsPdf([c], fileName(f, "Brochure") + ".pdf");
}

// ============ 4) Company booklet — multi-page profile ============
function miniContact(ctx, W, H) {
  const y = H - 128;
  line(ctx, 104, y, W - 104, LINE, 1);
  ctx.textBaseline = "alphabetic"; ctx.textAlign = "left";
  setFont(ctx, SANS, "700", 21, 0.5); ctx.fillStyle = INK;
  ctx.fillText(`+91 ${CONTACT.phone}   ·   ${CONTACT.email}`, 104, y + 36);
  setFont(ctx, SANS, "600", 20); ctx.fillStyle = BLUE;
  ctx.fillText(`www.${WEBSITE}   ·   www.${WEBSITE2}`, 104, y + 68);
  try { ctx.letterSpacing = "0px"; } catch { /* */ }
  setFont(ctx, SANS, "400", 19); ctx.fillStyle = INK_SOFT;
  ctx.fillText(CONTACT.address, 104, y + 98);
}

function pageHead(ctx, W, kicker, title) {
  fill(ctx, 0, 0, W, A4_H, CREAM);
  fill(ctx, 0, 0, W, 12, BRAND);
  label(ctx, kicker, 104, 150, BRAND);
  const tb = para(ctx, title, 104, 236, W - 208, { fam: DISPLAY, weight: "700", size: 78, lh: 82, col: INK, maxLines: 2 });
  const ry = tb + 36;
  line(ctx, 104, ry, W - 104, LINE, 2);
  return ry + 92;
}
function dotGrid(ctx, x, y, cols, rows, gap, col) {
  ctx.fillStyle = col;
  for (let r = 0; r < rows; r++) for (let cI = 0; cI < cols; cI++) { ctx.beginPath(); ctx.arc(x + cI * gap, y + r * gap, 3.2, 0, 7); ctx.fill(); }
}
async function framedPhoto(ctx, url, x, y, w, h, bracket) {
  if (bracket) { fill(ctx, x - 26, y - 26, 170, 16, YELLOW); fill(ctx, x - 26, y - 26, 16, 170, YELLOW); }
  const img = await loadImage(url);
  if (img) drawCover(ctx, img, x, y, w, h); else fill(ctx, x, y, w, h, "#dbe2ec");
}
function pageHeadBrand(ctx, W, title, dark, sub) {
  fill(ctx, 0, 0, W, A4_H, dark ? BLUE : "#ffffff");
  setFont(ctx, SANS, "800", 74, 0.5); ctx.fillStyle = dark ? "#ffffff" : INK; ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  ctx.fillText(title.toUpperCase(), 110, 190);
  try { ctx.letterSpacing = "0px"; } catch { /* */ }
  line(ctx, 110, 224, 360, YELLOW, 6);
  let y = 320;
  if (sub) { setFont(ctx, SANS, "600", 34); ctx.fillStyle = dark ? "rgba(255,255,255,0.9)" : INK_SOFT; ctx.fillText(sub, 110, 284); y = 372; }
  return y;
}
function miniContactLight(ctx, W, H) {
  const y = H - 128;
  line(ctx, 110, y, W - 110, "rgba(255,255,255,0.28)", 1);
  ctx.textBaseline = "alphabetic"; ctx.textAlign = "left";
  setFont(ctx, SANS, "700", 21, 0.5); ctx.fillStyle = "#ffffff";
  ctx.fillText(`+91 ${CONTACT.phone}   ·   ${CONTACT.email}`, 110, y + 36);
  setFont(ctx, SANS, "600", 20); ctx.fillStyle = YELLOW;
  ctx.fillText(`www.${WEBSITE}   ·   www.${WEBSITE2}`, 110, y + 68);
  try { ctx.letterSpacing = "0px"; } catch { /* */ }
  setFont(ctx, SANS, "400", 19); ctx.fillStyle = "rgba(255,255,255,0.8)";
  ctx.fillText(CONTACT.address, 110, y + 98);
}
export async function downloadBooklet() {
  await ensureFonts();
  const W = A4_W, H = A4_H, pages = [];
  const H1 = (ctx, t, x, y, col, size = 74) => { setFont(ctx, SANS, "800", size, 0.5); ctx.fillStyle = col; ctx.textAlign = "left"; ctx.textBaseline = "alphabetic"; ctx.fillText(t.toUpperCase(), x, y); try { ctx.letterSpacing = "0px"; } catch { /* */ } };

  // ---- P1 Cover (white, photo-driven, blue+yellow motifs) ----
  {
    const c = newCanvas(W, H), ctx = c.getContext("2d");
    fill(ctx, 0, 0, W, H, "#ffffff");
    fill(ctx, W - 150, 0, 150, 150, BLUE);
    const logo = await loadImage(LOGO);
    if (logo) ctx.drawImage(logo, 80, 70, 128, 128);
    setFont(ctx, SANS, "800", 40); ctx.fillStyle = INK; ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
    ctx.fillText("INDIAN TRANSPORT ORGANISATION", 236, 120);
    setFont(ctx, SANS, "800", 34); ctx.fillText("ITO ENGINEERING INFRA PVT LTD", 236, 168);
    setFont(ctx, SANS, "700", 22, 2); ctx.fillStyle = BLUE; ctx.fillText("CARGO LOGISTICS", 236, 210);
    try { ctx.letterSpacing = "0px"; } catch { /* */ }
    line(ctx, 236, 224, 470, BLUE, 3);
    await framedPhoto(ctx, IMAGES.airCargo, 80, 300, W - 160, 540, false);
    dotGrid(ctx, W - 360, 350, 6, 6, 26, "#cfd6df");
    fill(ctx, W - 96, 720, 18, 230, YELLOW);
    await framedPhoto(ctx, IMAGES.lehConvoy, 360, 880, W - 440, 360, true);
    dotGrid(ctx, 130, 980, 5, 6, 26, "#cfd6df");
    H1(ctx, "Company Profile", 80, 1500, INK, 96);
    setFont(ctx, SANS, "500", 30); ctx.fillStyle = "#333"; ctx.textAlign = "left";
    ctx.fillText(`www.${WEBSITE}`, 84, 1568);
    ctx.fillText(`www.${WEBSITE2}`, 84, 1612);
    pages.push(c);
  }

  // ---- P2 Company Overview (blue) ----
  {
    const c = newCanvas(W, H), ctx = c.getContext("2d");
    fill(ctx, 0, 0, W, H, BLUE);
    await framedPhoto(ctx, IMAGES.heroTruck, 150, 130, W - 300, 540, true);
    dotGrid(ctx, W - 260, 600, 5, 4, 24, "rgba(255,255,255,0.85)");
    H1(ctx, "Company Overview", 110, 780, "#ffffff", 74);
    line(ctx, 110, 812, 470, YELLOW, 6);
    setFont(ctx, SANS, "700", 34); ctx.fillStyle = "#ffffff"; ctx.fillText("About Us", 110, 892);
    const rows = [`Since ${SINCE}`, ...STATS.map((s) => `${s.value}${s.suffix} ${s.label}`), "Every pincode across India"];
    setFont(ctx, SANS, "500", 32); ctx.fillStyle = "rgba(255,255,255,0.95)";
    rows.forEach((r, i) => { fill(ctx, 110, 956 + i * 60 - 14, 30, 6, YELLOW); ctx.fillText(r, 158, 956 + i * 60); });
    miniContactLight(ctx, W, H);
    pages.push(c);
  }

  // ---- P3 Why ITO (white) ----
  {
    const c = newCanvas(W, H), ctx = c.getContext("2d");
    fill(ctx, 0, 0, W, H, "#ffffff");
    H1(ctx, "Why ITO", 110, 190, INK, 74);
    line(ctx, 110, 222, 360, YELLOW, 6);
    para(ctx, "ITO Engineering Infra Pvt. Ltd. is an industry-leading provider of integrated air & surface logistics to every pincode of India — the fastest, safest and most reliable transporter in the country, moving machinery, defence, ODC and project cargo since " + SINCE + ".", 110, 300, W - 220, { size: 32, lh: 48, col: INK_SOFT });
    fill(ctx, 0, 1120, W, 220, BLUE);
    await framedPhoto(ctx, IMAGES.odcTransformer, 110, 700, W - 220, 520, true);
    dotGrid(ctx, 130, 640, 6, 2, 24, "#cfd6df");
    miniContact(ctx, W, H);
    pages.push(c);
  }

  // ---- P4 Services (white) ----
  {
    let c = newCanvas(W, H), ctx = c.getContext("2d");
    let y = pageHeadBrand(ctx, W, "OUR SERVICES");
    SERVICES.forEach((s, i) => {
      if (y > H - 300) { miniContact(ctx, W, H); pages.push(c); c = newCanvas(W, H); ctx = c.getContext("2d"); y = pageHeadBrand(ctx, W, "OUR SERVICES"); }
      setFont(ctx, SANS, "800", 44); ctx.fillStyle = BLUE; ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
      ctx.fillText(String(i + 1).padStart(2, "0"), 110, y);
      setFont(ctx, SANS, "700", 36); ctx.fillStyle = INK; ctx.fillText(s.title, 210, y);
      y = para(ctx, s.short, 210, y + 44, W - 320, { size: 27, lh: 38, col: INK_SOFT });
      y += 44; line(ctx, 110, y, W - 110, "#e4e4e4", 1); y += 48;
    });
    miniContact(ctx, W, H);
    pages.push(c);
  }

  // ---- P5 Fleet (blue) ----
  {
    let c = newCanvas(W, H), ctx = c.getContext("2d");
    let y = pageHeadBrand(ctx, W, "OUR FLEET", true, "All types of vehicles");
    FLEET.forEach((v) => {
      if (y > H - 220) { miniContactLight(ctx, W, H); pages.push(c); c = newCanvas(W, H); ctx = c.getContext("2d"); y = pageHeadBrand(ctx, W, "OUR FLEET", true, "All types of vehicles"); }
      fill(ctx, 110, y - 26, 30, 6, YELLOW);
      setFont(ctx, SANS, "700", 34); ctx.fillStyle = "#ffffff"; ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
      ctx.fillText(v.name, 158, y);
      y = para(ctx, v.desc, 158, y + 40, W - 268, { size: 25, lh: 35, col: "rgba(255,255,255,0.9)" });
      y += 42; line(ctx, 110, y, W - 110, "rgba(255,255,255,0.25)", 1); y += 46;
    });
    miniContactLight(ctx, W, H);
    pages.push(c);
  }

  // ---- P6 Contact (blue) ----
  {
    const c = newCanvas(W, H), ctx = c.getContext("2d");
    fill(ctx, 0, 0, W, H, BLUE);
    const logo = await loadImage(LOGO);
    if (logo) { fill(ctx, 104, 130, 128, 128, "#fff"); ctx.drawImage(logo, 112, 138, 112, 112); }
    H1(ctx, "Move it with ITO", 104, 470, "#ffffff", 82);
    line(ctx, 104, 506, 420, YELLOW, 6);
    para(ctx, "Pan-India transport to every pincode, all types of vehicles, by road and air — fastest, safest and most reliable.", 104, 590, W - 240, { size: 32, lh: 46, col: "rgba(255,255,255,0.92)" });
    let y = 820;
    setFont(ctx, SANS, "800", 40, 1); ctx.fillStyle = "#fff"; ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
    ctx.fillText(`+91 ${CONTACT.phone}`, 104, y); try { ctx.letterSpacing = "0px"; } catch { /* */ }
    setFont(ctx, SANS, "500", 30); ctx.fillStyle = "rgba(255,255,255,0.92)";
    ctx.fillText(CONTACT.email, 104, y + 56);
    ctx.fillStyle = YELLOW; ctx.fillText(`www.${WEBSITE}   ·   www.${WEBSITE2}`, 104, y + 110);
    setFont(ctx, SANS, "400", 26); ctx.fillStyle = "rgba(255,255,255,0.85)";
    para(ctx, CONTACT.address, 104, y + 164, W - 208, { size: 26, lh: 36, col: "rgba(255,255,255,0.85)" });
    pages.push(c);
  }

  saveCanvasesAsPdf(pages, "ITO - Company Profile Booklet.pdf");
}
